import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { ROLE_LABELS } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import {
  LayoutDashboard, FolderKanban, ClipboardList, CalendarCheck, FileText,
  FolderOpen, Bell, Users, Search, LogOut, Mail, Building2, Receipt, Globe, UserCheck, Briefcase, Workflow as WorkflowIcon, Trophy, KeyRound, FileSignature, ChevronDown, Menu, X
} from "lucide-react";

const LOGO_URL = "https://customer-assets-jai6qajn.emergentagent.net/job_performance-tracker-140/artifacts/swckjj4a_LD%20Logo.png";

// ---------------------------------------------------------------------------
// Sidebar navigation. Structure: a mix of top-level items and grouped items
// (each group has its own routable parent PLUS a dropdown of child links).
// Visibility flags (adminOnly / hrAllowed / hrHidden / employeeHidden) are
// applied by `filterNavItem` below and cascade to children.
// ---------------------------------------------------------------------------
const NAV = [
  {
    type: "group", key: "dashboard",
    to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard",
    children: [
      { to: "/workflow", label: "Workflow", icon: WorkflowIcon, testid: "nav-workflow", employeeHidden: true, hrHidden: true },
      { to: "/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard", employeeHidden: true, hrHidden: true },
    ],
  },
  {
    type: "group", key: "jobs",
    to: "/jobs", label: "Jobs", icon: FolderKanban, testid: "nav-jobs", hrHidden: true,
    children: [
      { to: "/daily-entries", label: "Daily Entries", icon: ClipboardList, testid: "nav-daily", hrHidden: true },
      { to: "/integrations", label: "Integrations", icon: KeyRound, testid: "nav-integrations", adminOnly: true },
    ],
  },
  {
    type: "group", key: "clients",
    to: "/clients", label: "Clients", icon: Building2, testid: "nav-clients", employeeHidden: true, hrHidden: true,
    children: [
      { to: "/invoices", label: "Invoices", icon: FileText, testid: "nav-invoices", adminOnly: true },
      { to: "/email", label: "Email", icon: Mail, testid: "nav-email", employeeHidden: true, hrHidden: true },
    ],
  },
  {
    type: "group", key: "teams",
    to: "/vendors", label: "Teams", icon: Briefcase, testid: "nav-vendors", employeeHidden: true, hrHidden: true,
    children: [
      { to: "/team", label: "Members", icon: Users, testid: "nav-team" },
    ],
  },
  {
    type: "group", key: "organizations",
    to: "/organizations", label: "Organizations", icon: Globe, testid: "nav-orgs", adminOnly: true,
    children: [
      { to: "/users", label: "Employees", icon: Users, testid: "nav-users", adminOnly: true, hrAllowed: true },
      { to: "/documentation", label: "Documentation", icon: FileSignature, testid: "nav-documentation" },
      { to: "/expenses", label: "Expenses", icon: Receipt, testid: "nav-expenses", adminOnly: true },
    ],
  },
  // Top-level items with no children
  { to: "/attendance", label: "Attendance", icon: CalendarCheck, testid: "nav-attendance" },
  { to: "/files", label: "Files", icon: FolderOpen, testid: "nav-files", adminOnly: true },
  { to: "/reminders", label: "Reminders", icon: Bell, testid: "nav-reminders", adminOnly: true },
  { to: "/pending-approvals", label: "Pending Approvals", icon: UserCheck, testid: "nav-pending-approvals", adminOnly: true, hrAllowed: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [activeOrgId, setActiveOrgId] = useState(localStorage.getItem("active_org_id") || "");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const isAdmin = user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin";
  const teamRole = (user?.team_role || "").toLowerCase();
  const isHr = !isAdmin && (teamRole === "hr" || (user?.role || "").toLowerCase() === "hr_manager");
  const isBareEmployee = !isAdmin && !isHr && (teamRole === "employee" || teamRole === "" || teamRole === "member");

  // Role gate — applies to both parents and children uniformly.
  const canSee = (n) => {
    if (isAdmin) return true;
    if (isHr) {
      if (n.adminOnly && !n.hrAllowed) return false;
      if (n.hrHidden) return false;
      return true;
    }
    if (n.adminOnly) return false;
    if (n.employeeHidden && isBareEmployee) return false;
    return true;
  };

  // Build the sidebar tree: each group is either kept as { parent, children }
  // (if parent OR at least one child is visible) or dropped entirely.
  const visibleNav = NAV.map((n) => {
    if (n.type !== "group") return canSee(n) ? n : null;
    const parentVisible = canSee(n);
    const visibleChildren = (n.children || []).filter(canSee);
    if (!parentVisible && visibleChildren.length === 0) return null;
    return { ...n, children: visibleChildren, parentVisible };
  }).filter(Boolean);

  // Track which groups are open. Auto-open the one containing the current route.
  const isRouteInGroup = (g) => {
    const paths = [g.to, ...(g.children || []).map((c) => c.to)];
    return paths.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  };
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    NAV.forEach((n) => { if (n.type === "group") initial[n.key] = false; });
    return initial;
  });
  useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      NAV.forEach((n) => {
        if (n.type === "group" && isRouteInGroup(n)) next[n.key] = true;
      });
      return next;
    });
     
  }, [location.pathname]);
  const toggleGroup = (key) => setOpenGroups((s) => ({ ...s, [key]: !s[key] }));

  // Close the mobile drawer whenever the route changes.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
  // Lock body scroll while drawer is open + close on Escape.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [drawerOpen]);

  useEffect(() => {
    if (!isAdmin) return;  // /organizations is admin-only — skip the call to avoid a 403 in the console
    api.get("/organizations").then(({ data }) => setOrgs(data || [])).catch(() => {});
    // Cross-tab sync of the org selector
    const onStorage = (e) => {
      if (e.key === "active_org_id") setActiveOrgId(e.newValue || "");
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [isAdmin]);

  const pickOrg = (val) => {
    setActiveOrgId(val);
    if (val) localStorage.setItem("active_org_id", val);
    else localStorage.removeItem("active_org_id");
    // Force a refresh of the currently-mounted page so its data refetches with the new org filter.
    window.dispatchEvent(new CustomEvent("active-org-changed", { detail: { org_id: val } }));
    window.location.reload();
  };

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim().length >= 2) {
      setMobileSearchOpen(false);
      navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    }
  };

  // Shared nav tree — rendered inside the desktop sidebar AND the mobile drawer.
  const renderNavTree = (idSuffix = "") => (
    <>
      {visibleNav.map((n) => {
        // Standalone item (no children)
        if (n.type !== "group") {
          return (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid + idSuffix}
              end
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-sm font-medium border-l-4 transition-colors ${
                  isActive
                    ? "border-[#2E8B57] bg-[#EAF3EE] text-[#1F5F3F] font-bold"
                    : "border-transparent text-zinc-600 hover:bg-[#F5F1E8] hover:text-[#1F5F3F]"
                }`
              }
            >
              <n.icon className="w-4 h-4" strokeWidth={2.25} />
              {n.label}
            </NavLink>
          );
        }

        // Grouped item — routable parent + collapsible children.
        const open = !!openGroups[n.key];
        const activePath = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
        return (
          <div key={n.key} data-testid={`nav-group-${n.key}${idSuffix}`}>
            <div className={`flex items-stretch border-l-4 ${activePath ? "border-[#2E8B57] bg-[#EAF3EE]" : "border-transparent"}`}>
              {n.parentVisible ? (
                <NavLink
                  to={n.to}
                  data-testid={n.testid + idSuffix}
                  end
                  className={({ isActive }) =>
                    `flex-1 flex items-center gap-3 pl-5 pr-2 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "text-[#1F5F3F] font-bold"
                        : "text-zinc-600 hover:text-[#1F5F3F]"
                    }`
                  }
                >
                  <n.icon className="w-4 h-4" strokeWidth={2.25} />
                  {n.label}
                </NavLink>
              ) : (
                <div className="flex-1 flex items-center gap-3 pl-5 pr-2 py-2.5 text-sm font-medium text-zinc-400" title="You don't have access to the parent page — but you can open its subtabs below.">
                  <n.icon className="w-4 h-4" strokeWidth={2.25} />
                  {n.label}
                </div>
              )}
              {n.children.length > 0 && (
                <button
                  type="button"
                  onClick={() => toggleGroup(n.key)}
                  className="px-3 flex items-center text-zinc-500 hover:text-[#1F5F3F] hover:bg-[#F5F1E8]"
                  aria-label={open ? `Collapse ${n.label}` : `Expand ${n.label}`}
                  aria-expanded={open}
                  data-testid={`nav-group-toggle-${n.key}${idSuffix}`}
                >
                  <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2.25} />
                </button>
              )}
            </div>
            {open && n.children.length > 0 && (
              <div className="bg-[#F5F1E8]/60" data-testid={`nav-group-children-${n.key}${idSuffix}`}>
                {n.children.map((c) => (
                  <NavLink
                    key={c.to}
                    to={c.to}
                    data-testid={c.testid + idSuffix}
                    end
                    className={({ isActive }) =>
                      `flex items-center gap-3 pl-12 pr-4 py-2.5 text-[13px] font-medium border-l-4 transition-colors ${
                        isActive
                          ? "border-[#B8860B] bg-[#FBF3DE] text-[#8B6508] font-bold"
                          : "border-transparent text-zinc-600 hover:bg-[#F5F1E8] hover:text-[#1F5F3F]"
                      }`
                    }
                  >
                    <c.icon className="w-3.5 h-3.5" strokeWidth={2.25} />
                    {c.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </>
  );

  const userFooter = (
    <div className="border-t border-zinc-900 p-4">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 bg-[#E5C46B] border border-[#1B2A25] flex items-center justify-center font-mono text-xs font-bold text-[#1B2A25]">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-bold truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="label-mono truncate" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role] || user?.role}</div>
        </div>
      </div>
      <button
        onClick={logout}
        data-testid="logout-btn"
        className="w-full flex items-center justify-center gap-2 brut-btn-secondary px-3 py-2 text-xs"
      >
        <LogOut className="w-3.5 h-3.5" /> SIGN OUT
      </button>
    </div>
  );

  // Bottom tab bar (mobile) — 4 role-aware destinations + "Menu" (opens drawer).
  const hasJobs = visibleNav.some((n) => n.key === "jobs" && n.parentVisible);
  const bottomTabs = [
    { to: "/dashboard", label: "Home", icon: LayoutDashboard, testid: "bottomnav-home" },
    hasJobs
      ? { to: "/jobs", label: "Jobs", icon: FolderKanban, testid: "bottomnav-jobs" }
      : { to: "/users", label: "Employees", icon: Users, testid: "bottomnav-users" },
    { to: "/attendance", label: "Attendance", icon: CalendarCheck, testid: "bottomnav-attendance" },
    { to: "/documentation", label: "Docs", icon: FileSignature, testid: "bottomnav-docs" },
  ];

  return (
    <div className="min-h-screen bg-[#F4F1EA] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-60 bg-white border-r border-[#1B2A25] flex-col sticky top-0 h-screen">
        <Link to="/dashboard" className="px-2 py-6 border-b border-[#1B2A25] flex flex-col items-center gap-2 bg-white relative" data-testid="brand-logo">
          <img src={LOGO_URL} alt="Land Drafters" className="w-full h-auto object-contain max-h-28" />
          <div className="text-[10px] font-mono tracking-[0.22em] text-[#1B2A25] leading-tight" data-testid="brand-tagline">
            Organize | Track | Improve
          </div>
          <div className="text-[10px] font-mono italic text-[#4A5952] leading-tight tracking-wide" data-testid="brand-slogan">
            Every Activity Matters.
          </div>
          <div className="ld-brand-strip absolute bottom-0 left-0 right-0" aria-hidden="true" />
        </Link>

        <nav className="flex-1 py-4 overflow-y-auto">
          {renderNavTree()}
        </nav>
        {userFooter}
      </aside>

      {/* Mobile slide-out drawer */}
      {drawerOpen && (
        <div className="fixed inset-0 z-[95] lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-overlay" />
          <aside className="absolute left-0 top-0 bottom-0 w-[280px] max-w-[85vw] bg-white border-r border-[#1B2A25] flex flex-col shadow-2xl animate-in slide-in-from-left duration-200">
            <div className="px-4 py-3 border-b border-[#1B2A25] flex items-center justify-between bg-white relative">
              <img src={LOGO_URL} alt="Land Drafters" className="h-12 w-auto object-contain" />
              <button onClick={() => setDrawerOpen(false)} className="p-2 -mr-1 text-zinc-600 hover:text-[#1F5F3F]" aria-label="Close menu" data-testid="mobile-drawer-close">
                <X className="w-5 h-5" />
              </button>
              <div className="ld-brand-strip absolute bottom-0 left-0 right-0" aria-hidden="true" />
            </div>
            <nav className="flex-1 py-3 overflow-y-auto overscroll-contain">
              {renderNavTree("-m")}
            </nav>
            {userFooter}
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-[#1B2A25] h-14 lg:h-16 flex items-center justify-between px-3 sm:px-4 lg:px-6 sticky top-0 z-40">
          <div className="ld-brand-strip absolute bottom-0 left-0 right-0" aria-hidden="true" />
          {/* Mobile: hamburger + logo */}
          <div className="flex items-center gap-2 lg:hidden min-w-0">
            <button
              onClick={() => setDrawerOpen(true)}
              className="p-2 -ml-1 text-[#1B2A25] hover:text-[#1F5F3F]"
              aria-label="Open menu"
              data-testid="mobile-menu-btn"
            >
              <Menu className="w-6 h-6" strokeWidth={2.25} />
            </button>
            <Link to="/dashboard" className="shrink-0" data-testid="mobile-brand-logo">
              <img src={LOGO_URL} alt="Land Drafters" className="h-9 w-auto object-contain" />
            </Link>
          </div>
          {/* Desktop search */}
          <form onSubmit={onSearch} className="hidden lg:block flex-1 max-w-xl">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search jobs, files, invoices, people…"
                data-testid="global-search-input"
                className="w-full brut-input pl-9 pr-3 py-2 text-sm font-mono"
              />
            </div>
          </form>
          <div className="flex items-center gap-1.5 sm:gap-3 lg:ml-6">
            <button
              onClick={() => setMobileSearchOpen((v) => !v)}
              className="lg:hidden p-2 text-zinc-600 hover:text-[#1F5F3F]"
              aria-label="Search"
              data-testid="mobile-search-btn"
            >
              <Search className="w-5 h-5" strokeWidth={2.25} />
            </button>
            <NotificationBell />
            {orgs.length > 0 && (
              <label className="hidden sm:flex items-center gap-2 text-xs font-mono border border-[#1B2A25] px-2 py-1 bg-[#FBF3DE]" data-testid="org-switcher-wrap">
                <Globe className="w-3.5 h-3.5 text-[#8B6508]" />
                <span className="hidden lg:inline text-[#8B6508]">ORG:</span>
                <select
                  value={activeOrgId}
                  onChange={(e) => pickOrg(e.target.value)}
                  className="bg-transparent font-mono text-xs font-bold focus:outline-none cursor-pointer max-w-[90px]"
                  data-testid="org-switcher"
                >
                  <option value="">All Orgs</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>{o.short_code || o.name}</option>
                  ))}
                </select>
              </label>
            )}
            <div className="label-mono hidden md:block">SESSION ACTIVE</div>
            <div className="w-2 h-2 bg-[#2E8B57] border border-[#1B2A25] shrink-0" title="Live"></div>
          </div>
        </header>

        {/* Mobile search bar (expands under the header) */}
        {mobileSearchOpen && (
          <div className="lg:hidden bg-white border-b border-[#1B2A25] px-3 py-2 sticky top-14 z-30" data-testid="mobile-search-bar">
            <form onSubmit={onSearch}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search jobs, files, invoices, people…"
                  data-testid="mobile-search-input"
                  autoFocus
                  className="w-full brut-input pl-9 pr-3 py-2.5 text-sm font-mono"
                />
              </div>
            </form>
          </div>
        )}

        <main className="flex-1 p-3 sm:p-4 lg:p-8 pb-24 lg:pb-8 min-w-0">
          <Outlet />
        </main>

        {/* Mobile bottom tab bar */}
        <nav
          className="lg:hidden fixed bottom-0 left-0 right-0 z-[80] bg-white border-t-2 border-[#1B2A25] grid grid-cols-5"
          style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          data-testid="bottom-nav"
        >
          {bottomTabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-mono font-bold tracking-tight transition-colors ${
                  isActive ? "text-[#1F5F3F] bg-[#EAF3EE]" : "text-zinc-500 hover:text-[#1F5F3F]"
                }`
              }
            >
              <t.icon className="w-5 h-5" strokeWidth={2.25} />
              {t.label}
            </NavLink>
          ))}
          <button
            onClick={() => setDrawerOpen(true)}
            className={`flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-mono font-bold tracking-tight ${drawerOpen ? "text-[#1F5F3F] bg-[#EAF3EE]" : "text-zinc-500 hover:text-[#1F5F3F]"}`}
            data-testid="bottomnav-more"
          >
            <Menu className="w-5 h-5" strokeWidth={2.25} />
            More
          </button>
        </nav>
      </div>
    </div>
  );
}
